import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/money";
import { chargeRuleLabel } from "@/lib/chargeRuleLabels";
import {
  VISITOR_PAYMENT_MODE_OPTIONS,
  type VisitorPaymentMode,
  type VisitorPriceQuote,
  type VisitorRegistrationCreatePayload,
} from "@/lib/visitorRegistration";
import type { Contract } from "@/types/camping";

interface CreateResponse {
  registration: { id: string; invoice_number?: string | null };
  invoice?: { invoice_number: string } | null;
}

const EMPTY_FORM = {
  contract_id: "",
  visit_date: new Date().toISOString().slice(0, 10),
  adults: "1",
  children: "0",
  extra_cars: "0",
  payment_mode: "direct" as VisitorPaymentMode,
  notes: "",
  collect_payment: false,
  payment_method: "" as "" | "cash" | "card",
};

export interface RegisterVisitorModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialContractId?: string;
}

function contractToOption(contract: Contract): AutocompleteOption<Contract> {
  return {
    key: contract.id,
    label: `${contract.guest_name ?? "Gast"} – ${contract.pitch_code ?? "Platz"}`,
    description: `Vertrag ${contract.contract_type === "permanent" ? "Dauercamper" : contract.contract_type}`,
    value: contract,
  };
}

export function RegisterVisitorModal({
  open,
  onClose,
  onCreated,
  initialContractId,
}: RegisterVisitorModalProps) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [contractOption, setContractOption] = useState<AutocompleteOption<Contract> | null>(null);
  const [quote, setQuote] = useState<VisitorPriceQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadContracts = useCallback(() => {
    Promise.all([
      api.get<Contract[]>("/api/v1/contracts?status=active"),
      api.get<Contract[]>("/api/v1/contracts?status=notice"),
    ])
      .then(([active, notice]) =>
        setContracts(
          [...active, ...notice].filter(
            (c) => c.contract_type === "permanent" && (c.status === "active" || c.status === "notice"),
          ),
        ),
      )
      .catch(() => setContracts([]));
  }, []);

  const loadContractOptions = useCallback(
    (query: string) => {
      const q = query.trim().toLowerCase();
      const filtered = contracts.filter((contract) => {
        if (!q) return true;
        const haystack = [
          contract.guest_name,
          contract.pitch_code,
          contract.booking_number,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
      return Promise.resolve(filtered.map(contractToOption));
    },
    [contracts],
  );

  const partyValid = useMemo(() => {
    const adults = Number(form.adults) || 0;
    const children = Number(form.children) || 0;
    const extraCars = Number(form.extra_cars) || 0;
    return adults + children + extraCars > 0;
  }, [form.adults, form.children, form.extra_cars]);

  useEffect(() => {
    if (!open) return;
    loadContracts();
    setForm({
      ...EMPTY_FORM,
      contract_id: initialContractId ?? "",
    });
    setContractOption(null);
    setQuote(null);
    setQuoteError(null);
    setError(null);
    setSuccessMessage(null);
    setSubmitting(false);
  }, [open, initialContractId, loadContracts]);

  useEffect(() => {
    if (!open || !form.contract_id || !partyValid) {
      setQuote(null);
      setQuoteError(null);
      return;
    }

    const timer = window.setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError(null);
      api
        .post<VisitorPriceQuote>("/api/v1/visitor-registrations/quote", {
          contract_id: form.contract_id,
          visit_date: form.visit_date,
          adults: Number(form.adults) || 0,
          children: Number(form.children) || 0,
          extra_cars: Number(form.extra_cars) || 0,
        })
        .then(setQuote)
        .catch((e: Error) => {
          setQuote(null);
          setQuoteError(e.message);
        })
        .finally(() => setQuoteLoading(false));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [
    open,
    form.contract_id,
    form.visit_date,
    form.adults,
    form.children,
    form.extra_cars,
    partyValid,
  ]);

  useEffect(() => {
    if (!open || !initialContractId || contracts.length === 0) return;
    const contract = contracts.find((c) => c.id === initialContractId);
    if (contract) {
      setContractOption(contractToOption(contract));
      setForm((f) => ({ ...f, contract_id: contract.id }));
    }
  }, [open, initialContractId, contracts]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      const payload: VisitorRegistrationCreatePayload = {
        contract_id: form.contract_id,
        visit_date: form.visit_date,
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
        extra_cars: Number(form.extra_cars) || 0,
        payment_mode: form.payment_mode,
        notes: form.notes.trim() || null,
      };
      if (form.payment_mode === "direct" && form.collect_payment && form.payment_method) {
        payload.collect_payment = true;
        payload.payment_method = form.payment_method;
      }
      const result = await api.post<CreateResponse>("/api/v1/visitor-registrations", payload);
      const invoiceNumber = result.invoice?.invoice_number;
      setSuccessMessage(
        invoiceNumber
          ? `Besucher angemeldet. Rechnung ${invoiceNumber} wurde erstellt${
              payload.collect_payment ? " und bezahlt" : ""
            }.`
          : "Besucher angemeldet und auf die Jahresendabrechnung gebucht.",
      );
      onCreated();
      if (!payload.collect_payment) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anmelden");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Besucher anmelden"
      description="Besucher für einen Dauercamper erfassen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <LockedAutocomplete
          label="Dauercamper-Vertrag"
          required
          placeholder="Gast oder Platz suchen…"
          value={contractOption}
          loadOptions={loadContractOptions}
          onChange={(option) => {
            setContractOption(option);
            setForm((f) => ({ ...f, contract_id: option?.value.id ?? "" }));
          }}
        />

        <label className="flex flex-col gap-1 text-sm">
          Besuchsdatum
          <input
            type="date"
            className="eh-input"
            required
            value={form.visit_date}
            onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
          />
        </label>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm">
            Besucher Erwachsene
            <input
              type="number"
              min={0}
              className="eh-input"
              value={form.adults}
              onChange={(e) => setForm((f) => ({ ...f, adults: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Besucher Kinder
            <input
              type="number"
              min={0}
              className="eh-input"
              value={form.children}
              onChange={(e) => setForm((f) => ({ ...f, children: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Zusätzliche Autos
            <input
              type="number"
              min={0}
              className="eh-input"
              value={form.extra_cars}
              onChange={(e) => setForm((f) => ({ ...f, extra_cars: e.target.value }))}
            />
          </label>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Abrechnung</legend>
          {VISITOR_PAYMENT_MODE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer gap-3 rounded-xl border border-separator p-3 text-sm"
            >
              <input
                type="radio"
                name="payment_mode"
                className="mt-1"
                checked={form.payment_mode === option.value}
                onChange={() =>
                  setForm((f) => ({
                    ...f,
                    payment_mode: option.value,
                    collect_payment: option.value === "direct" ? f.collect_payment : false,
                    payment_method: option.value === "direct" ? f.payment_method : "",
                  }))
                }
              />
              <span>
                <span className="font-medium">{option.label}</span>
                <span className="mt-0.5 block text-label-secondary">{option.description}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {form.payment_mode === "direct" ? (
          <div className="space-y-2 rounded-xl border border-separator p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.collect_payment}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    collect_payment: e.target.checked,
                    payment_method: e.target.checked ? f.payment_method || "cash" : "",
                  }))
                }
              />
              Zahlung direkt erfassen
            </label>
            {form.collect_payment ? (
              <label className="flex flex-col gap-1 text-sm">
                Zahlungsart
                <select
                  className="eh-select-input"
                  value={form.payment_method}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      payment_method: e.target.value as "" | "cash" | "card",
                    }))
                  }
                >
                  <option value="cash">Bar</option>
                  <option value="card">Karte</option>
                </select>
              </label>
            ) : null}
          </div>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          Notiz (optional)
          <textarea
            className="eh-input min-h-[4rem]"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </label>

        {quoteLoading ? (
          <p className="text-sm text-label-secondary">Preis wird berechnet…</p>
        ) : quoteError ? (
          <p className="text-sm text-status-red">{quoteError}</p>
        ) : quote ? (
          <div className="rounded-xl border border-separator p-3 text-sm">
            <p className="mb-2 font-medium">Voraussichtliche Gebühren</p>
            <ul className="space-y-1 text-label-secondary">
              {quote.lines.map((line) => (
                <li key={`${line.rule_type}-${line.article_id}`} className="flex justify-between gap-4">
                  <span>
                    {chargeRuleLabel(line.rule_type)}: {line.quantity} × {line.article_name}
                  </span>
                  <span className="tabular-nums text-label">{formatCurrency(line.line_total)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 flex justify-between gap-4 border-t border-separator pt-2 font-medium">
              <span>Gesamt (brutto)</span>
              <span className="tabular-nums">{formatCurrency(quote.total)}</span>
            </p>
          </div>
        ) : null}

        {successMessage ? <p className="text-sm text-status-green">{successMessage}</p> : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {successMessage ? "Schließen" : "Abbrechen"}
          </Button>
          {!successMessage ? (
            <Button
              type="submit"
              loading={submitting}
              disabled={!form.contract_id || !partyValid || !!quoteError || quoteLoading}
            >
              Besucher anmelden
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
